import { getAdminOrgId } from "./admin";
import { getApiBase, getAuthHeaders } from "./auth";

const API_BASE = getApiBase();

function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type Tag = {
  id: string;
  name: string;
  color: string | null;
  description: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  ambassador_org_ids: string[];
};

export type TagCreate = {
  name: string;
  color?: string | null;
  description?: string | null;
  ambassador_org_ids?: string[];
};

export type TagUpdate = {
  name?: string;
  color?: string | null;
  description?: string | null;
  ambassador_org_ids?: string[] | null;
};

export type OrganizationTag = {
  id: string;
  organization_id: string;
  tag_id: string;
  tag: Tag;
  created_at: string;
  updated_at: string;
};

export type TagList = {
  items: Tag[];
  total: number;
};

export async function listTags(params?: {
  query?: string;
  ambassador_org_id?: string;
}): Promise<TagList> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.query) queryParams.append("q", params.query);
  if (params?.ambassador_org_id) queryParams.append("ambassador_org_id", params.ambassador_org_id);

  const response = await fetch(`${API_BASE}/tags?${queryParams.toString()}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load tags");
  }
  return response.json();
}

export async function createTag(payload: TagCreate): Promise<Tag> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/tags`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Failed to create tag";
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function updateTag(tagId: string, payload: TagUpdate): Promise<Tag> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/tags/${tagId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update tag");
  }
  return response.json();
}

export async function deleteTag(tagId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/tags/${tagId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error("Failed to delete tag");
  }
}

export async function listOrganizationTags(orgId: string): Promise<OrganizationTag[]> {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/tags/organizations/${orgId}`, {
    headers: defaultHeaders(adminOrgId),
  });
  if (!response.ok) {
    throw new Error("Failed to load organization tags");
  }
  return response.json();
}

export async function addTagToOrganization(orgId: string, tagId: string): Promise<OrganizationTag> {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/tags/organizations/${orgId}/tags?tag_id=${tagId}`, {
    method: "POST",
    headers: defaultHeaders(adminOrgId),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = "Failed to add tag";
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.detail || errorMessage;
    } catch {
      errorMessage = errorText || errorMessage;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function removeTagFromOrganization(orgId: string, tagId: string): Promise<void> {
  const adminOrgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/tags/organizations/${orgId}/tags/${tagId}`, {
    method: "DELETE",
    headers: defaultHeaders(adminOrgId),
  });
  if (!response.ok) {
    throw new Error("Failed to remove tag");
  }
}
